// client/src/features/documents/enums.ts — the two vocabularies SCR-DOC-001/002 render · DOC-004.
//
// `DocumentKind` is a GENERATED union (`@/client`), declared `as const satisfies readonly T[]` —
// the shape `features/equipment/enums.ts` and `features/repairs/enums.ts` established: the array
// is a literal tuple the filter and the form can iterate, and a member the contract adds or drops
// is a compile error in the PR that regenerated the client rather than a `<select>` that quietly
// stops offering a type. The order is 03 §12.2's own declaration order, with `OTHER` last because
// it is the catch-all and a picker that offered it second would invite it.
//
// `DocumentLinkTarget` is NOT a generated union and cannot be one: 03 §12.2 declares
// `linked_entity_type` as a `varchar` held by a CHECK, because the seventeen targets span two
// schemas and three modules with no common CLR type (`DECLARATION` is a `fiscal.declarations`
// row, `ORGANIZATION` is the tenant itself). So the list is written out here from
// `DocumentLinkTargets.All`, in that file's own order, and `__tests__/contract-parity.test.ts`
// reads the C# and fails the build on a difference. A string that is not on this list is a 422
// `document-link-target-unknown` — the slice refuses it before the CHECK does.

import type { DocumentKind } from '@/client';
import { fiscalVisible } from '@/config/fiscal';
import { invoicesVisible } from '@/config/invoices';

/** 03 §12.2's `DocumentKind`, in the enum's own order. */
export const DOCUMENT_KINDS = [
  'CONTRACT',
  'AMENDMENT',
  'INVOICE',
  'RECEIPT',
  'ID_DOCUMENT',
  'INSURANCE_POLICY',
  'CERTIFICATE',
  'METER_PHOTO',
  'DECLARATION_PDF',
  'DECLARATION_XML',
  'RECIPISA',
  'OTHER',
] as const satisfies readonly DocumentKind[];

/**
 * Whether a document kind belongs to a module withdrawn from view (D-2780): the supplier invoice
 * while the invoices are, the two declarations and the recipisă while the fiscal module is. Such
 * a document keeps its kind and its row; the lists that draw documents leave it out, and no
 * select offers the kind.
 */
export function isWithdrawnKind(kind: DocumentKind | null | undefined): boolean {
  if (kind === 'INVOICE') return !invoicesVisible();
  if (kind === 'DECLARATION_PDF' || kind === 'DECLARATION_XML' || kind === 'RECIPISA') {
    return !fiscalVisible();
  }
  return false;
}

/** The kinds offered where a person chooses one — the upload's select and the hub's kind filter. */
export function offeredDocumentKinds(): DocumentKind[] {
  return DOCUMENT_KINDS.filter((kind) => !isWithdrawnKind(kind));
}

/** The seventeen values `ck_documents_linked_type` admits — `DocumentLinkTargets.All`. */
export const DOCUMENT_LINK_TARGETS = [
  'PROPERTY',
  'UNIT',
  'CONTRACT',
  'TENANT',
  'OWNER',
  'EXPENSE',
  'INVOICE',
  'EQUIPMENT',
  'MAINTENANCE_TASK',
  'REPAIR_TICKET',
  'INSURANCE',
  'METER_READING',
  'DECLARATION',
  'ANAF_SUBMISSION',
  'OWNER_TENANCY',
  'BANK_STATEMENT_IMPORT',
  'ORGANIZATION',
] as const;

export type DocumentLinkTarget = (typeof DOCUMENT_LINK_TARGETS)[number];

/**
 * The targets the UPLOAD form offers, and the only ones it offers.
 *
 * A form needs to name an ENTITY, and naming one needs a picker over a list endpoint that exists
 * on this line. Three do: properties, contracts and tenants. The other fourteen are written by
 * the flows that own them — a meter photograph by SCR-UTIL-002, a repair photograph by
 * SCR-REP-002, an invoice scan by SCR-DOC-003 — and none of them is a document a landlord files
 * by hand from the documents screen. See DEVIATIONS D-1141.
 *
 * The organization itself was the fourth until D-2574: „Documentele mele" lists what concerns the
 * owner and not the organization, so a paper filed against the organization from that screen
 * would have vanished from the one screen that filed it.
 */
export const UPLOADABLE_LINK_TARGETS = [
  'PROPERTY',
  'CONTRACT',
  'TENANT',
] as const satisfies readonly DocumentLinkTarget[];

export type UploadableLinkTarget = (typeof UPLOADABLE_LINK_TARGETS)[number];

/**
 * Whether a target is one the upload form can offer a picker for.
 *
 * It guards the ONE place the distinction still bites: `useEntityOptions` takes the four and
 * `linkedEntityType` now admits all seventeen, because a panel that opens ON a row needs no
 * picker and no list — it knows the id, renders a sentence in place of the two `<select>`s and
 * fetches nothing (D-2021). The false arm is unreachable while the picker is the only thing
 * that sets the field with no link fixed, and it is written down rather than cast away, so that
 * a future caller which does set it cannot reach a list that was never meant for it (D-2233).
 */
export function isUploadableTarget(value: string): value is UploadableLinkTarget {
  return (UPLOADABLE_LINK_TARGETS as readonly string[]).includes(value);
}

/**
 * The MIME families 04 §8.10 asks the detail screen to preview: „preview (pdf/img) streamed via
 * §9.10". Everything else is offered as a download, which is what a `.xlsx` deserves.
 *
 * The type read is the VERSION's `mimeType` — what `ContentTypeSniffer` decided from the bytes,
 * never the client's header and never the extension (02 §9.3 step 3).
 */
export function isPreviewable(mimeType: string): boolean {
  return mimeType === 'application/pdf' || mimeType.startsWith('image/');
}
